import React, {useMemo} from 'react';
import {motion} from 'framer-motion';
import {classNames} from 'utils';
import {isDesktop} from 'react-device-detect';

type ButtonVariantType = 'primary' | 'secondary' | 'alert';
type ButtonStateType = ButtonVariantType | 'disabled' | 'loading';

const BUTTON_STATE_MAP: Record<ButtonStateType, string> = {
  primary: classNames('bg-[#34bb78] active:bg-green-500 active:scale-95', isDesktop && 'hover:bg-green-500'),
  secondary: classNames('bg-white active:bg-slate-100 border border-[#34bb78] active:scale-95', isDesktop && 'hover:bg-slate-100'),
  alert: classNames('bg-red-500 active:bg-red-400 active:scale-95', isDesktop && 'hover:bg-red-400'),
  disabled: 'bg-slate-400',
  loading: 'bg-slate-400',
};

const BUTTON_TEXT_STATE_MAP: Record<ButtonStateType, string> = {
  primary: 'text-white',
  secondary: 'text-[#34bb78]',
  alert: 'text-white',
  disabled: 'text-white',
  loading: 'text-white',
};

interface IButtonProps {
  text: string;
  onClick?: () => void;
  variant?: ButtonVariantType;
  size?: 'normal' | 'small';
  tooltip?: string;
  disabled?: boolean;
  loading?: boolean;
  rounded?: boolean;
  fullWidth?: boolean;
}

const Button: React.FC<IButtonProps> = ({
  text,
  onClick,
  variant = 'primary',
  size = 'normal',
  tooltip = '',
  disabled = false,
  loading = false,
  rounded = false,
  fullWidth = false,
}) => {
  const state: ButtonStateType = useMemo(() => {
    if (loading) return 'loading';
    if (disabled) return 'disabled';
    return variant;
  }, [loading, disabled, variant]);

  return (
    <button
      title={tooltip}
      type={onClick ? 'button' : 'submit'}
      onClick={() => onClick?.()}
      disabled={loading || disabled}
      className={classNames(
        'cursor-pointer flex justify-center items-center transition-all duration-200',
        rounded ? 'rounded-full py-4' : 'rounded-md py-2',
        fullWidth && 'w-full',
        size === 'normal' ? 'px-10' : 'px-8',
        BUTTON_STATE_MAP[state],
      )}>
      <p
        className={classNames(
          'text-center font-semibold',
          size === 'normal' ? 'text-md' : 'text-sm',
          BUTTON_TEXT_STATE_MAP[state],
          loading && 'opacity-0',
        )}>
        {text}
      </p>
      {loading && (
        <motion.div
          className="absolute flex justify-center items-center w-5 h-5 bg-slate-200 rounded-full"
          animate={{rotate: 360}}
          transition={{repeat: Infinity, duration: 1, ease: 'linear'}}>
          <div className={`w-[80%] h-[80%] rounded-full ${BUTTON_STATE_MAP['loading']}`}>
            <div className={`w-[25%] h-[25%] rounded-md ${BUTTON_STATE_MAP['loading']}`}></div>
          </div>
        </motion.div>
      )}
    </button>
  );
};

export default Button;
